import { ScoreBadge, type ScoreThresholds } from "@aircall/blocks";

import { PERCENTAGE_MAX } from "@/constants/percentage.constants";
import { getColorFromRange } from "@/helpers/range/range.helpers";
import { ColorScale } from "@/helpers/range/range.helpers.decl";

/** Inclusive lower bound of the high CSAT band. */
export const CSAT_HIGH_FROM = 71;
/** Inclusive lower bound of the medium CSAT band. */
export const CSAT_MEDIUM_FROM = 41;

export type CsatRating = "high" | "medium" | "low" | "na";

type CsatScoreProps = {
  // Null is "Not applicable"
  value: number | null;
};

/**
 * CSAT tone bands, preserving the local behaviour: high [71, 100] -> success,
 * medium [41, 71) -> warning, low [0, 41) -> destructive (not
 * destructive-strong). Values are matched top-down by their `from` bound.
 */
const CSAT_THRESHOLDS: ScoreThresholds = [
  { from: CSAT_HIGH_FROM, tone: "success" },
  { from: CSAT_MEDIUM_FROM, tone: "warning" },
  { from: 0, tone: "destructive" },
];

const csatScale: ColorScale<Exclude<CsatRating, "na">> = {
  high: [CSAT_HIGH_FROM, PERCENTAGE_MAX],
  medium: [CSAT_MEDIUM_FROM, CSAT_HIGH_FROM],
  low: [0, CSAT_MEDIUM_FROM],
};

export function getCsatRating(value: number | null): CsatRating {
  if (value === null) {
    return "na";
  }

  if (value < 0) {
    return "low";
  }

  if (value > PERCENTAGE_MAX) {
    return "high";
  }

  return getColorFromRange(csatScale)(value) ?? "na";
}

export type CsatTextColor =
  | "text-success"
  | "text-warning"
  | "text-destructive"
  | "text-muted-foreground";

const ratingTextColor: Record<CsatRating, CsatTextColor> = {
  high: "text-success",
  medium: "text-warning",
  low: "text-destructive",
  na: "text-muted-foreground",
};

export function getCsatRatingTextColor(rating: CsatRating): CsatTextColor {
  return ratingTextColor[rating];
}

/**
 * Thin adapter over the `@aircall/blocks` `ScoreBadge`, rendering the CSAT
 * score as a plain rounded integer (no percentage suffix) using the CSAT tone
 * bands. Finite out-of-range values are no longer clamped by the badge
 * (150 -> "150", -10 -> "-10"); only null renders the muted "--".
 */
export function CsatScore({ value }: CsatScoreProps) {
  return (
    <ScoreBadge
      value={value}
      thresholds={CSAT_THRESHOLDS}
      formatOptions={{ maximumFractionDigits: 0 }}
      data-test={`csat-score-${value}`}
    />
  );
}
